import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { AnimatePresence } from "framer-motion";
import { toast } from "react-toastify";
import { submitInquiry } from "../../store/slices/inquiriesSlice";
import {
  Backdrop,
  Card,
  CloseBtn,
  Eyebrow,
  Title,
  Description,
  Form,
  Input,
  SubmitBtn,
  SecondaryBtn,
  SuccessBlock,
} from "./styles/NewsletterPopup.styles";

const STORAGE_KEY = "luma_newsletter_popup_v1";
const SHOW_DELAY_MS = 8000;

function NewsletterPopup() {
  const dispatch = useDispatch();
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (window.localStorage.getItem(STORAGE_KEY)) return;
    const t = setTimeout(() => setOpen(true), SHOW_DELAY_MS);
    return () => clearTimeout(t);
  }, []);

  const dismiss = () => {
    try {
      window.localStorage.setItem(STORAGE_KEY, "dismissed");
    } catch {}
    setOpen(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSubmitting(true);
    try {
      await dispatch(
        submitInquiry({
          fullName: "Newsletter Subscriber",
          email: email.trim(),
          message: "Newsletter subscription (popup)",
          source: "newsletter",
        }),
      ).unwrap();
      setSuccess(true);
      try {
        window.localStorage.setItem(STORAGE_KEY, "subscribed");
      } catch {}
      setTimeout(() => setOpen(false), 2400);
      toast.success("Thank you for subscribing!");
    } catch {
      toast.error("Subscription failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const backdropVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { duration: 0.25 } },
    exit: { opacity: 0, transition: { duration: 0.2 } },
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 20, scale: 0.96 },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: { duration: 0.4, ease: [0.16, 1, 0.3, 1] },
    },
    exit: {
      opacity: 0,
      y: 10,
      scale: 0.97,
      transition: { duration: 0.25 },
    },
  };

  return (
    <AnimatePresence>
      {open && (
        <Backdrop
          variants={backdropVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
          onClick={dismiss}
          role="dialog"
          aria-modal="true"
          aria-label="Newsletter signup"
        >
          <Card
            variants={cardVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            onClick={(e) => e.stopPropagation()}
          >
            <CloseBtn onClick={dismiss} aria-label="Close">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M6 6l12 12M18 6L6 18" />
              </svg>
            </CloseBtn>

            {success ? (
              <SuccessBlock>
                <h3>You're In</h3>
                <p>
                  Thank you for subscribing. Watch your inbox for new arrivals.
                </p>
              </SuccessBlock>
            ) : (
              <>
                <Eyebrow>Welcome Aboard</Eyebrow>
                <Title>
                  Step into Our <em>Inner Circle</em>
                </Title>
                <Description>
                  Get first access to new arrivals, exclusive listings, and
                  market insights from our team, straight to your inbox.
                </Description>
                <Form onSubmit={handleSubmit}>
                  <Input
                    type="email"
                    placeholder="your@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoFocus
                    aria-label="Email address"
                  />
                  <SubmitBtn type="submit" disabled={submitting}>
                    {submitting ? "Subscribing..." : "Subscribe"}
                  </SubmitBtn>
                  <SecondaryBtn type="button" onClick={dismiss}>
                    Maybe later
                  </SecondaryBtn>
                </Form>
              </>
            )}
          </Card>
        </Backdrop>
      )}
    </AnimatePresence>
  );
}

export default NewsletterPopup;
